import React, { CSSProperties, ReactNode, useEffect, useMemo, useRef } from "react";
import { Asset, CardData, DeckInfo, DeckRule, FontStyleOverride, Layer, ProjectFontDefinition } from "./types";
import { substituteIconsInText } from "./cardTextIcons";

type CgoCardSideProps = {
  w?: number;
  h?: number;
  cardId: string;
  deckInfo: DeckInfo;
  printing?: boolean | string;
  style?: CSSProperties;
  hiddenLayers?: string[];
  children?: ReactNode;
  globalAssets: { [key: string]: any };
  isRev?: boolean;
  isDebug?: boolean;
  projectFonts?: Array<ProjectFontDefinition>;
  deckFontOverrides?: Array<FontStyleOverride>;
  cardFontOverrides?: Array<FontStyleOverride>;
  useHighResImages?: boolean;
};

function CgoCardSide({
  cardId,
  deckInfo,
  printing,
  style,
  hiddenLayers,
  children,
  globalAssets,
  w,
  h,
  isRev,
  isDebug,
  projectFonts,
  deckFontOverrides,
  cardFontOverrides,
  useHighResImages = false,
}: CgoCardSideProps) {
  function replaceRuleVarsFromCardData(rule: DeckRule, cardData: CardData): DeckRule[] {
    const layerVariants = getVarValuesForRuleField(rule.layer, cardData);
    const argVariants = getVarValuesForRuleField(rule.arg, cardData);
    const arg2Variants = getVarValuesForRuleField(rule.arg2, cardData);

    const longestVariants = Math.max(layerVariants.length, argVariants.length, arg2Variants.length);

    let results = [];
    for (let i = 0; i < longestVariants; i++) {
      results.push({
        id: rule.id,
        rule: rule.rule,
        layer: layerVariants[i] ?? layerVariants[0], //try to add values from the same row in sheet, if not present then add from first row
        arg: argVariants[i] ?? argVariants[0],
        arg2: arg2Variants[i] ?? arg2Variants[0],
      });
    }

    return results;
  }

  function getVarValuesForRuleField(ruleField: string | undefined, cardData: CardData): string[] {
    if (ruleField === undefined || cardData === undefined) {
      return [];
    }

    // Match all occurrences of #(key)
    const regex = /#\(([^)]+)\)/g;
    const matches = [...ruleField.matchAll(regex)];

    if (matches.length === 0) {
      return [ruleField];
    }

    // Start with the original ruleField as the only entry
    let results = [ruleField];

    for (const match of matches) {
      const fullMatch = match[0]; // e.g., "#(key)"
      const key = match[1]; // e.g., "key"
      let values = cardData[key];

      if (!values || values.length === 0) {
        return [];
      }

      // For each current string in results, replace one instance of this variable
      results = results.flatMap((str) =>
        values
          .filter((x) => x !== undefined && x !== null) // Filter out undefined/null values
          .map((value) => str.replace(fullMatch, value)),
      );
    }

    return results;
  }

  if (!deckInfo || !deckInfo.cardInfo) {
    return <div>Deck info missing (missing .psd?)</div>;
  }

  let cardW = w ?? deckInfo?.cardInfo?.w;
  let cardH = h ?? deckInfo?.cardInfo?.h;
  let zIndex = 10 + deckInfo?.layers?.length;
  const rules: DeckRule[] = useMemo(() => {
    let rules: DeckRule[] = [];
    if (!deckInfo?.cardInfo) {
      return [];
    }
    if (!Array.isArray(deckInfo?.rules)) {
      return [];
    }
    let targetRules = deckInfo.rules;
    if (deckInfo.rulesRev && isRev) {
      targetRules = deckInfo.rulesRev;
    }
    for (let rule of targetRules) {
      if (rule.condition) {
        let conditionPassed = true;
        try {
          JSON.parse(rule.condition);
        } catch (e) {
          console.error("Invalid JSON in rule id:", rule.id, "condition:", rule.condition);
          conditionPassed = false;
          continue;
        }
        let condition = JSON.parse(rule.condition) as { [key: string]: string[] };
        for (let colName in condition) {
          let rulePassed = false;
          let passingValues = condition[colName];
          for (let value of passingValues) {
            if (deckInfo.cards[cardId] && deckInfo.cards[cardId][colName]?.includes(value)) {
              rulePassed = true;
              break;
            }
          }
          if (!rulePassed) {
            conditionPassed = false;
            break;
          }
        }
        if (!conditionPassed) {
          continue;
        }
      }
      rules = rules.concat(replaceRuleVarsFromCardData(rule, deckInfo.cards[cardId]));
    }
    return rules;
  }, [cardId, deckInfo, printing, style?.borderRadius, hiddenLayers, children, globalAssets, w, h, isRev]);

  let overlayEnabled = useMemo(() => {
    for (let rule of rules) {
      if (rule.layer == "__global") {
        if (rule.rule == "disableOverlay") {
          return false;
        }
      }
    }
    return true;
  }, [deckInfo, rules]);

  const bleed = useMemo(() => {
    if (printing) {
      return 0; // no bleed for printing
    }
    for (let rule of rules) {
      if (rule.layer == "__global") {
        if (rule.rule == "bleed") {
          return rule.arg ? parseInt(rule.arg) : 0;
        }
      }
    }
    return 0;
  }, [deckInfo, rules]);

  if (!deckInfo.cardInfo) {
    return <div>Card info missing (missing .psd?)</div>;
  }

  const filteredDeckFontOverrides = useMemo(() => {
    let filteredOverrides: Array<FontStyleOverride> = [];
    for (let override of deckFontOverrides ?? []) {
      if (override.conditionParameter && override.conditionValue) {
        let splitConditionValues = override.conditionValue.split(",").map((val) => val.trim());
        if (
          !deckInfo.cards[cardId] ||
          !deckInfo.cards[cardId][override.conditionParameter] ||
          !splitConditionValues.some((val) => deckInfo.cards[cardId][override.conditionParameter]?.includes(val))
        ) {
          continue;
        }
      }
      filteredOverrides.push(override);
    }
    return filteredOverrides;
  }, [deckFontOverrides, deckInfo.cards, cardId]);

  const filteredCardFontOverrides = useMemo(() => {
    let filteredOverrides: Array<FontStyleOverride> = [];
    for (let override of cardFontOverrides ?? []) {
      if (override.conditionParameter && override.conditionValue) {
        let splitConditionValues = override.conditionValue.split(",").map((val) => val.trim());
        if (
          !deckInfo.cards[cardId] ||
          !deckInfo.cards[cardId][override.conditionParameter] ||
          !splitConditionValues.some((val) => deckInfo.cards[cardId][override.conditionParameter]?.includes(val))
        ) {
          continue;
        }
      }
      filteredOverrides.push(override);
    }
    return filteredOverrides;
  }, [cardFontOverrides, deckInfo.cards, cardId]);

  const layers = useMemo(() => {
    for (let rule of rules) {
      if (rule.layer == "__global") {
        if (rule.rule == "flatImage" && rule.arg) {
          return [
            <CgoCardSideLayer
              rules={[
                { layer: "flatImage", rule: "set_image", arg: rule.arg },
                { layer: "flatImage", rule: "css", arg: "backgroundColor", arg2: rule.arg2 || "transparent" },
              ]}
              assets={Object.assign({}, globalAssets, deckInfo.assets ?? {})}
              layer={{
                name: "flatImage",
                x: 0,
                y: 0,
                w: cardW,
                h: cardH,
                imgUrl: rule.arg || "",
              }}
              zIndex={5}
              key={"layer-" + "flatImage" + rule.arg}
            />,
          ];
        }
      }
    }
    let layers: ReactNode[] = [];
    for (let layer of deckInfo.layers) {
      if (hiddenLayers?.includes(layer.name)) {
        continue;
      }
      layers.push(
        <CgoCardSideLayer
          rules={rules.filter((rule) => rule.layer === layer.name) || []}
          assets={Object.assign({}, globalAssets, deckInfo.assets ?? {})}
          bleed={bleed}
          layer={layer}
          zIndex={zIndex}
          key={"layer-" + layer.name}
          isDebug={isDebug}
          projectFonts={projectFonts}
          deckFontOverrides={filteredDeckFontOverrides}
          cardFontOverrides={filteredCardFontOverrides}
          useHighResImages={useHighResImages}
        />,
      );
      --zIndex;
    }
    return layers;
  }, [
    rules,
    globalAssets,
    deckInfo.assets,
    deckInfo.layers,
    cardW,
    cardH,
    hiddenLayers,
    bleed,
    zIndex,
    isDebug,
    projectFonts,
    filteredDeckFontOverrides,
    filteredCardFontOverrides,
    useHighResImages,
  ]);

  /* I have no idea how this fixes the printing, but I spent
  north of 8 hours on print being messed up, and this works. So DON'T TOUCH IT!
  Update: this magic number messed up printing. Returning to 0 */
  let magicMove = 0; // 100000;
  let marginTop = printing ? "-" + magicMove + "px" : 0;
  //let marginLeft = marginTop;
  let transform = printing ? "translate(" + /*magicMove + "px, "*/ "0px, " + magicMove + "px)" : "";
  if (printing == "onePerSheet") {
    marginTop = /*marginLeft =*/ 0;
    transform = "";
  }

  let styleFromDeckInfo = {
    width: cardW - bleed * 2,
    height: cardH - bleed * 2,
    paddingTop: -bleed + "px",
    marginTop,
    transform: transform,
  };

  let finalStyle = Object.assign({ padding: -(bleed ?? 0) + "px" }, styleFromDeckInfo, style || {});

  return (
    <div className={"card-side-wrap overlay-" + (overlayEnabled ? "enabled" : "disabled")} style={finalStyle}>
      {layers}
    </div>
  );
}

type CgoCardSideLayerProps = {
  rules: DeckRule[];
  assets: { [key: string]: Asset };
  layer: Layer;
  zIndex: number;
  isDebug?: boolean;
  bleed?: number;
  projectFonts?: Array<ProjectFontDefinition>;
  deckFontOverrides?: Array<FontStyleOverride>;
  cardFontOverrides?: Array<FontStyleOverride>;
  useHighResImages?: boolean;
};

function CgoCardSideLayer({
  rules,
  assets,
  layer,
  zIndex,
  isDebug,
  bleed,
  projectFonts,
  deckFontOverrides,
  cardFontOverrides,
  useHighResImages = false,
}: CgoCardSideLayerProps) {
  const sortedDeckFontOverrides = deckFontOverrides
    ? [...deckFontOverrides].sort(
        (a, b) => a.layerName.localeCompare(b.layerName) || a.conditionOrder - b.conditionOrder,
      )
    : [];

  const sortedCardFontOverrides = cardFontOverrides
    ? [...cardFontOverrides].sort(
        (a, b) => a.layerName.localeCompare(b.layerName) || a.conditionOrder - b.conditionOrder,
      )
    : [];

  let htmlString = "";
  let display = false;
  let displayBackground = false;
  let innerStyle: CSSProperties = {};
  let ruleStyleDefinitions: { [key: string]: string } = {};

  function camelToKebabCase(camel: string) {
    return camel.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`);
  }

  function applyOverrides(layerOverride: FontStyleOverride) {
    if (!layerOverride) return;

    innerStyle = Object.assign({}, innerStyle, {
      fontFamily: layerOverride.fontFamily || innerStyle.fontFamily,
      fontWeight: layerOverride.fontWeight || innerStyle.fontWeight,
      fontStyle: layerOverride.fontStyle || innerStyle.fontStyle,
      fontSize: layerOverride.fontSize || innerStyle.fontSize,
      color: layerOverride.fontColor || innerStyle.color,
      lineHeight: layerOverride.lineHeight || innerStyle.lineHeight,
      WebkitTextStrokeWidth: layerOverride.strokeWidth || innerStyle.WebkitTextStrokeWidth,
      WebkitTextStrokeColor: layerOverride.strokeColor || innerStyle.WebkitTextStrokeColor,
      margin: layerOverride.margin || innerStyle.margin,
      textAlign: layerOverride.textAlign || innerStyle.textAlign,
    });

    if (layerOverride.fontSize) {
      fontSize = layerOverride.fontSize;
      overrideFontSizeFromRules = true;
    }
    if (layerOverride.iconHeight) {
      iconHeight = layerOverride.iconHeight;
    }
    if (layerOverride.iconMargin) {
      iconMargin = layerOverride.iconMargin;
    }
  }

  const fontSizeRules = [];
  for (let rule of rules) {
    switch (rule.rule) {
      case "set_innerHTML":
        if (rule.arg) {
          htmlString += rule.arg + " ";
          display = true;
        }
        break;
      case "layer_visible":
        displayBackground = true;
        display = true;
        break;
      case "css":
        if (rule.arg && rule.arg2) {
          if (CSS.supports(camelToKebabCase(rule.arg), rule.arg2)) {
            ruleStyleDefinitions[rule.arg] = rule.arg2;
          } else {
            console.warn(`Invalid CSS property value: ${rule.arg}: ${rule.arg2}`);
          }
        }
        break;
      case "set_image":
        if (rule.arg && assets[rule.arg]) {
          innerStyle.backgroundImage = `url(${assets[rule.arg].url})`;
          display = true;
        }
        break;
      case "set_font": {
        let fontName = rule.arg;
        innerStyle.fontFamily = fontName + (innerStyle.fontFamily ?? "");
        break;
      }
      case "long_text_size":
        if (rule.arg && rule.arg2) {
          fontSizeRules.push({ length: +rule.arg, size: rule.arg2 });
        }
        break;
      case "project_font":
        if (rule.arg && projectFonts) {
          let projectFont = projectFonts.find((font) => font.fontName === rule.arg);
          if (projectFont) {
            innerStyle.fontFamily = projectFont.fontFamily;
            innerStyle.fontSize = projectFont.fontSize;
            innerStyle.fontWeight = projectFont.fontWeight;
            innerStyle.fontStyle = projectFont.fontStyle;
            innerStyle.color = projectFont.fontColor;
          }
        }
        break;
      default:
        break;
    }
  }

  innerStyle = Object.assign({}, innerStyle, ruleStyleDefinitions);
  let fontSize = innerStyle.fontSize;

  if (!display) {
    return <></>;
  }
  let innerContent = <></>;
  let textContent = <></>;
  let overrideFontSizeFromRules = false;
  let iconHeight: string | number | undefined = undefined;
  let iconMargin: string | undefined = undefined;

  const overrideSets = [sortedDeckFontOverrides, sortedCardFontOverrides];
  for (const overrides of overrideSets) {
    const layerOverrides = overrides.filter((o) => o.layerName === layer.name && !o.subtextId);
    for (const layerOverride of layerOverrides) {
      applyOverrides(layerOverride);
    }
  }

  if (htmlString.length) {
    textContent = innerContent = (
      <div
        className="cgo-card-text"
        style={Object.assign({}, innerStyle, { backgroundImage: undefined, paintOrder: "stroke" })}
        key="text-string"
      >
        {substituteIconsInText(
          htmlString,
          fontSize,
          assets,
          fontSizeRules,
          overrideFontSizeFromRules,
          iconHeight,
          sortedDeckFontOverrides.filter((o) => o.layerName === layer.name && o.subtextId),
          sortedCardFontOverrides.filter((o) => o.layerName === layer.name && o.subtextId),
          iconMargin,
        )}
      </div>
    );
  }

  if (innerStyle.backgroundImage) {
    innerStyle.width = "100%";
    innerStyle.height = "100%";
    innerStyle.backgroundSize = "contain";
    innerStyle.backgroundPosition = "center";
    innerStyle.backgroundRepeat = "no-repeat";
    innerContent = (
      <div className="cgo-card-image" style={innerStyle} key="image">
        {textContent}
      </div>
    );
  }

  const imageUrl = useHighResImages && layer.hiResImgUrl ? layer.hiResImgUrl : layer.imgUrl;

  const layerStyle = {
    left: layer.x - (bleed ?? 0),
    top: layer.y - (bleed ?? 0),
    width: layer.w,
    height: layer.h,
    zIndex: zIndex,
    backgroundImage: displayBackground ? `url("${imageUrl}")` : "",
    fontSize: ruleStyleDefinitions.fontSize,
  };

  return (
    <div className={"cgo-card-layer " + layer.name} style={layerStyle}>
      <div className="cgo-layer-outer">
        {isDebug && (
          <div className="cgo-layer-debug" style={{ zIndex: zIndex + 1 }}>
            {layer.name}
          </div>
        )}
        {innerContent}
      </div>
    </div>
  );
}

export default CgoCardSide;
